import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ShieldCheck, Activity, AlertCircle, CheckCircle2, 
  Users, FileText, TrendingUp, Clock, AlertTriangle,
  BarChart3, PieChart, Calendar, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DPODashboard = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
  const [recentBreaches, setRecentBreaches] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [analyticsRes, advancedRes, breachesRes, actionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/dpo/advanced`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/data-breaches`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketing-actions`, { withCredentials: true })
      ]);
      
      setAnalytics(analyticsRes.data);
      setAdvancedAnalytics(advancedRes.data);
      setRecentBreaches(Array.isArray(breachesRes.data) ? breachesRes.data.slice(0, 5) : []);
      setPendingActions(Array.isArray(actionsRes.data) ? actionsRes.data.filter(a => !a.dpo_approved).slice(0, 5) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAction = async (actionId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/marketing-actions/${actionId}/approve`, {}, { withCredentials: true });
      toast.success('Ação aprovada com sucesso');
      fetchAllData();
    } catch (error) {
      console.error('Error approving action:', error);
      toast.error('Erro ao aprovar ação');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      low: 'bg-green-50 text-green-700 border-green-200',
      medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      high: 'bg-orange-50 text-orange-700 border-orange-200',
      critical: 'bg-red-50 text-red-700 border-red-200'
    };
    return <Badge className={styles[severity] || styles.low}>{severity?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const complianceScore = advancedAnalytics?.compliance_score || 0;

  return (
    <div className="space-y-8" data-testid="dpo-dashboard">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard DPO
        </h1>
        <p className="text-slate-600">
          Bem-vindo, <span className="font-semibold">{user?.name}</span>. Supervisionar conformidade e auditorias RGPD.
        </p>
      </div>

      {/* Main Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card 
          className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/analytics')}
          data-testid="dpo-compliance-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Score de Conformidade</p>
                <div className="text-4xl font-bold text-[#10B981]">{complianceScore}%</div>
                <p className="text-xs text-blue-500 mt-1">Ver analytics →</p>
              </div>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/marketing-actions')}
          data-testid="dpo-pending-actions-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Ações Pendentes</p>
                <div className="text-4xl font-bold text-[#F59E0B]">{analytics?.marketing_actions?.pending_approval || 0}</div>
                <p className="text-xs text-blue-500 mt-1">Ver ações →</p>
              </div>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-[#F59E0B]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/consents')}
          data-testid="dpo-active-consents-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Consentimentos Ativos</p>
                <div className="text-4xl font-bold text-[#10B981]">{analytics?.consents?.active || 0}</div>
                <p className="text-xs text-blue-500 mt-1">Ver consentimentos →</p>
              </div>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/data-breaches')}
          data-testid="dpo-breaches-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Violações de Dados</p>
                <div className="text-4xl font-bold text-red-600">{advancedAnalytics?.breaches?.total || 0}</div>
                {advancedAnalytics?.breaches?.critical > 0 && (
                  <p className="text-xs text-red-500">{advancedAnalytics.breaches.critical} críticas</p>
                )}
                <p className="text-xs text-blue-500 mt-1">Ver violações →</p>
              </div>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Visão Geral
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Conformidade RGPD
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Aprovações Pendentes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Statistics Cards */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#10B981]" />
                  Estatísticas de Consentimentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div 
                    className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                    onClick={() => navigate('/consents')}
                    data-testid="dpo-active-consents-stat"
                  >
                    <span className="text-slate-700">Ativos</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-[#10B981]">{analytics?.consents?.active || 0}</span>
                      <p className="text-xs text-blue-500">Ver →</p>
                    </div>
                  </div>
                  <div 
                    className="flex justify-between items-center p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => navigate('/consents')}
                    data-testid="dpo-pending-consents-stat"
                  >
                    <span className="text-slate-700">Pendentes</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-[#F59E0B]">{analytics?.consents?.pending || 0}</span>
                      <p className="text-xs text-blue-500">Ver →</p>
                    </div>
                  </div>
                  <div 
                    className="flex justify-between items-center p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => navigate('/consents')}
                    data-testid="dpo-revoked-consents-stat"
                  >
                    <span className="text-slate-700">Revogados</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-red-600">{analytics?.consents?.revoked || 0}</span>
                      <p className="text-xs text-blue-500">Ver →</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Taxa de Consentimentos Ativos</span>
                      <span className="font-semibold text-[#0F172A]">{advancedAnalytics?.consents?.active_rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Breaches */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  Violações de Dados Recentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentBreaches.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                    <p>Nenhuma violação registada</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentBreaches.map((breach) => (
                      <div key={breach.breach_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-[#0F172A]">{breach.breach_type}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(breach.detected_at).toLocaleDateString('pt-PT')}
                          </p>
                        </div>
                        {getSeverityBadge(breach.severity)}
                      </div>
                    ))}
                    <Button 
                      variant="outline" 
                      className="w-full mt-2"
                      onClick={() => navigate('/data-breaches')}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Todas
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Users Overview */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Utilizadores do Sistema
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total de Utilizadores</span>
                    <span className="text-2xl font-bold text-[#0F172A]">{advancedAnalytics?.users?.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Utilizadores Ativos</span>
                    <span className="text-xl font-semibold text-[#10B981]">{advancedAnalytics?.users?.active || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Utilizadores Inativos</span>
                    <span className="text-xl font-semibold text-slate-400">{advancedAnalytics?.users?.inactive || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Marketing Actions Overview */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Ações de Marketing (Art. 30º)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total de Ações</span>
                    <span className="text-2xl font-bold text-[#0F172A]">{advancedAnalytics?.marketing_actions?.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Pendentes de Aprovação</span>
                    <span className="text-xl font-semibold text-[#F59E0B]">{advancedAnalytics?.marketing_actions?.pending_approval || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Taxa de Aprovação</span>
                    <span className="text-xl font-semibold text-[#10B981]">{advancedAnalytics?.marketing_actions?.approval_rate || 0}%</span>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => navigate('/marketing-actions')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Ações
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="compliance">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Conformidade RGPD</CardTitle>
              <CardDescription>
                Estado de cumprimento dos requisitos do Regulamento Geral sobre a Proteção de Dados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { article: 'Art. 6º', title: 'Base Legal', desc: 'Consentimentos devidamente documentados' },
                  { article: 'Art. 7º', title: 'Condições', desc: 'Consentimentos demonstráveis e específicos' },
                  { article: 'Art. 13º/14º', title: 'Informação', desc: 'Titulares devidamente informados' },
                  { article: 'Art. 15º-22º', title: 'Direitos', desc: 'Mecanismos para exercício de direitos' },
                  { article: 'Art. 30º', title: 'Registo', desc: 'Atividades de tratamento documentadas' },
                  { article: 'Art. 32º', title: 'Segurança', desc: 'Medidas técnicas implementadas' },
                  { article: 'Art. 33º', title: 'Notificação', desc: 'Violações reportadas à CNPD' },
                  { article: 'Art. 34º', title: 'Comunicação', desc: 'Titulares notificados quando necessário' },
                  { article: 'Art. 37º', title: 'DPO', desc: 'Encarregado de proteção de dados nomeado' }
                ].map((item) => (
                  <div key={item.article} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                      <h3 className="font-semibold text-[#0F172A]">{item.article} - {item.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
                Ações de Marketing Pendentes de Aprovação
              </CardTitle>
              <CardDescription>
                Ações que requerem validação do DPO antes de serem executadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingActions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
                  <p className="text-lg font-medium">Todas as ações estão aprovadas!</p>
                  <p className="text-sm">Não há ações pendentes de aprovação.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingActions.map((action) => (
                    <div key={action.action_id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#0F172A]">{action.action_name}</h4>
                          <p className="text-sm text-slate-600 mt-1">{action.purpose}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline">{action.action_type}</Badge>
                            <Badge variant="outline">{action.legal_basis}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Criada em: {new Date(action.created_at).toLocaleDateString('pt-PT')}
                          </p>
                        </div>
                        <Button 
                          onClick={() => handleApproveAction(action.action_id)}
                          className="bg-[#10B981] hover:bg-[#10B981]/90"
                          size="sm"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Aprovar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Recent Activities */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-slate-600" />
            Atividades Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {advancedAnalytics?.recent_activities?.length === 0 ? (
            <p className="text-center text-slate-500 py-4">Nenhuma atividade recente</p>
          ) : (
            <div className="space-y-3">
              {advancedAnalytics?.recent_activities?.slice(0, 8).map((activity, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                      <Activity className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0F172A]">{activity.action}</p>
                      <p className="text-xs text-slate-500">Por: {activity.performed_by}</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(activity.timestamp).toLocaleString('pt-PT')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DPODashboard;
